import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    Stack,
} from "@mui/material";
import {useEffect, useState} from "react";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import Tooltip from "@mui/material/Tooltip";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import {updateApplication} from "../../applicationDetail.actions";
import {useTranslation} from "react-i18next";
import {applicationInfoState} from "../../../searchms/state";
import {useRecoilState} from "recoil";
import { useForm } from "react-hook-form";
import {FormAutocomplete, FormTextField } from "../../../common/form-fields-wrappers";
import {ALL_COUNTRIES_PREFIX, CountryType} from "../../../data";
import dayjs from "dayjs";


import {useHasScope} from "../../../../../config/globalConfig";
import { NetworkScope } from "../../../types";
import { APPLICATION_STATES, APPLICATION_SUBSTATES_AGGREGATED, FINAL_STATES } from "../../config";
import { FISCAL_CODE_REG_EXP, FISCAL_CODE_REG_EXP_ES, FISCAL_CODE_REG_EXP_PT, LOCALE_DATE_TIME_HUMAN_FORMAT } from "../../../config";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const mockCurrentStatus = [
    APPLICATION_STATES.CREATED,
    APPLICATION_STATES.REJECTED_BY_AUTHORITIES,
    APPLICATION_STATES.SUSPENDED_BY_COMMITTEE,
    APPLICATION_STATES.ACCEPTED_BY_COMMITTEE,
    APPLICATION_STATES.REJECTED_BY_COMMITTEE,
    APPLICATION_STATES.NOT_APPROVED,
    APPLICATION_STATES.APPROVED,
];

const mockAreaOfApplicationStatus = [
    'VACANT',
    'TAKEN'
];

const mockNewPartner = [
    true,
    false
];

const mockRegionList = [
    "Valle d'Aosta",
    "Piemonte",
    "Lombardia",
    "Friuli Venezia Giulia",
    "Liguria",
    "Trentino Alto Adige",
    "Veneto",
    "Emilia Romagna",
    "Toscana",
    "Marche",
    "Umbria",
    "Lazio",
    "Campania",
    "Basilicata",
    "Molise",
    "Puglia",
    "Calabria",
    "Sicilia",
    "Sardegna",
    "Abbruzzo"

];

const Application = () => {
    const hasScope = useHasScope;
    const countriesPrefix:CountryType[] = ALL_COUNTRIES_PREFIX ;
    const [applicationData , setApplicationData] = useRecoilState(applicationInfoState);
    const [evaluationOutcomeValue, setEvaluationOutcomeValue] = useState(applicationData?.evaluationOutcome);
    const [isDisabled, setIsDisabled] = useState(true);
    const [evaluationOutcomeDisabled, setEvaluationOutcomeDisabled] = useState(true);
    const [evaluationOutcomeList, setEvaluationOutcomeList] = useState([""]);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    // Use State
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if(applicationData?.currentStatus) {
            setEvaluationOutcomeDisabled(isDisabled);
        
            if(Object.keys(APPLICATION_SUBSTATES_AGGREGATED).includes(applicationData.currentStatus)) {
                setEvaluationOutcomeList(APPLICATION_SUBSTATES_AGGREGATED[applicationData.currentStatus])
            } else{
                setEvaluationOutcomeValue(null);
                setEvaluationOutcomeDisabled(true);
            }
        }
    },[applicationData?.currentStatus])

    const handleDisableForm = () => {
        setIsDisabled(false);
        const isInFinalState = FINAL_STATES.includes(applicationData?.currentStatus)
        setEvaluationOutcomeDisabled(isInFinalState);
    }

    const { t } = useTranslation(['network']);
    const applicationForm = useForm({
        defaultValues: {
            applicationIdentifyingCode: applicationData?.applicationIdentifyingCode ?? "",
            communicatedToAuthoritiesDate: applicationData?.communicatedToAuthoritiesDate ?? '',
            currentStatus: applicationData?.currentStatus ?? null,
            areaOfApplicationStatus: applicationData?.areaOfApplicationStatus ?? "",
            evaluationOutcome: evaluationOutcomeValue,
            postalCode: applicationData?.postalCode ?? "",
            prefixPhoneNumber: {
                code: applicationData?.prefixPhoneNumber?.code ?? "",
                description: applicationData?.prefixPhoneNumber?.description ?? "",
                label: applicationData?.prefixPhoneNumber?.label ?? ""
            },
            applicant: {
                id: applicationData?.applicant?.id ?? 0,
                tenant: applicationData?.applicant?.tenant ?? "",
                name: applicationData?.applicant?.name ?? "",
                surname: applicationData?.applicant?.surname ?? "",
                companyName: applicationData?.applicant?.companyName ?? "",
                vatNumber: applicationData?.applicant?.vatNumber ?? "",
                siren: applicationData?.applicant?.siren ?? "",
                siret: applicationData?.applicant?.siret ?? "",
                taxCode: applicationData?.applicant?.taxCode ?? "",
                region: applicationData?.applicant?.region ?? "",
                address: applicationData?.applicant?.address ?? "",
                city: applicationData?.applicant?.city ?? "",
                province: applicationData?.applicant?.province ?? "",
                email: applicationData?.applicant?.email ?? "",
                phoneNumber: applicationData?.applicant?.phoneNumber ?? "",
                notes: applicationData?.applicant?.notes ?? "",
                newPartner: applicationData?.applicant?.newPartner ?? false,
            }

        },
        resolver: yupResolver(
            yup.object().shape({
                applicant: yup.object({
                    taxCode: yup
                        .string()
                        .when('tenant', {
                            is: "drivalia_es",
                            then: schema => schema.matches(FISCAL_CODE_REG_EXP_ES, {
                                message: 'common.error.invalidFS',
                                excludeEmptyString: true
                            }),
                        })
                        .when('tenant', {
                            is: "drivalia_pt",
                            then: schema => schema.matches(FISCAL_CODE_REG_EXP_PT, {
                                message: 'common.error.invalidFS',
                                excludeEmptyString: true
                            }),
                        }),
                }),
            })
        ),
    });
    const onSubmit = (data: any) => {
        setOpen(false);
        setIsDisabled(true);
        setEvaluationOutcomeDisabled(true);
        const reqParams = {
            id: applicationData?.id,
            applicationIdentifyingCode: data.applicationIdentifyingCode,
            currentStatus: data?.currentStatus ? t(`searchApplication.form.label.currentStatusValue.${data.currentStatus}`) : null,
            areaOfApplicationStatus: data.areaOfApplicationStatus != null ? t(`searchApplication.form.label.areaOfApplicationStatusValue.${data.areaOfApplicationStatus}`) : "",
            evaluationOutcome: data.evaluationOutcome,
            postalCode: data.postalCode,
            prefixPhoneNumber: {
                code: data.prefixPhoneNumber?.code,
                description: data.prefixPhoneNumber?.description ,
                label: data.prefixPhoneNumber?.label
            },
            applicant: {
                id: data.applicant?.id,
                tenant: data.applicant?.tenant,
                name: data.applicant?.name,
                surname: data.applicant?.surname,
                companyName: data.applicant?.companyName,
                vatNumber: data.applicant?.vatNumber,
                siren: data.applicant?.siren,
                siret: data.applicant?.siret,
                taxCode: data.applicant?.taxCode,
                region: data.applicant?.region,
                address: data.applicant?.address,
                city: data.applicant?.city,
                province: data.applicant?.province,
                email: data.applicant?.email,
                phoneNumber: data.applicant?.phoneNumber,
                notes: data.applicant?.notes,
                newPartner: data.applicant?.newPartner,
            }
        };
        // reqParams.currentStatus = t(`searchApplication.form.label.currentStatusValue.${data.currentStatus}`);
        // if(reqParams.areaOfApplicationStatus != null){
        //     reqParams.areaOfApplicationStatus = t(`searchApplication.form.label.areaOfApplicationStatusValue.${data.areaOfApplicationStatus}`);
        // }
        updateApplication(reqParams).then((response: any) => {
            setApplicationData(response.application);
        })
    }

    useEffect(() => {
        applicationForm.setFocus("applicationIdentifyingCode");
        applicationForm.setValue("applicationIdentifyingCode",applicationData?.applicationIdentifyingCode);
        applicationForm.setFocus("communicatedToAuthoritiesDate");
        applicationForm.setValue("communicatedToAuthoritiesDate", applicationData?.communicatedToAuthoritiesDate ? dayjs(applicationData.communicatedToAuthoritiesDate).format(LOCALE_DATE_TIME_HUMAN_FORMAT) : '')
        applicationForm.setFocus("currentStatus");
        applicationForm.setValue("currentStatus",applicationData?.currentStatus);
        applicationForm.setFocus("areaOfApplicationStatus");
        applicationForm.setValue("areaOfApplicationStatus",applicationData?.areaOfApplicationStatus);
        applicationForm.setFocus("evaluationOutcome");
        applicationForm.setValue("evaluationOutcome",applicationData?.evaluationOutcome);
        applicationForm.setFocus("postalCode");
        applicationForm.setValue("postalCode",applicationData?.postalCode);
        applicationForm.setFocus("applicant.id");
        applicationForm.setValue("applicant.id",applicationData?.applicant?.id);
        applicationForm.setFocus("applicant.tenant");
        applicationForm.setValue("applicant.tenant",applicationData?.applicant?.tenant);
        applicationForm.setFocus("applicant.name");
        applicationForm.setValue("applicant.name",applicationData?.applicant?.name);
        applicationForm.setFocus("applicant.surname");
        applicationForm.setValue("applicant.surname",applicationData?.applicant?.surname);
        applicationForm.setFocus("applicant.companyName");
        applicationForm.setValue("applicant.companyName",applicationData?.applicant?.companyName);
        applicationForm.setFocus("applicant.vatNumber");
        applicationForm.setValue("applicant.vatNumber",applicationData?.applicant?.vatNumber);
        applicationForm.setFocus("applicant.siren");
        applicationForm.setValue("applicant.siren",applicationData?.applicant?.siren);
        applicationForm.setFocus("applicant.siret");
        applicationForm.setValue("applicant.siret",applicationData?.applicant?.siret);
        applicationForm.setFocus("applicant.taxCode");
        applicationForm.setValue("applicant.taxCode",applicationData?.applicant?.taxCode);
        applicationForm.setFocus("applicant.region");
        applicationForm.setValue("applicant.region",applicationData?.applicant?.region);
        applicationForm.setFocus("applicant.address");
        applicationForm.setValue("applicant.address",applicationData?.applicant?.address);
        applicationForm.setFocus("applicant.city");
        applicationForm.setValue("applicant.city",applicationData?.applicant?.city);
        applicationForm.setFocus("applicant.province");
        applicationForm.setValue("applicant.province",applicationData?.applicant?.province);
        applicationForm.setFocus("applicant.email");
        applicationForm.setValue("applicant.email",applicationData?.applicant?.email);
        applicationForm.setFocus("prefixPhoneNumber");
        applicationForm.setValue("prefixPhoneNumber",applicationData?.prefixPhoneNumber);
        applicationForm.setFocus("applicant.phoneNumber");
        applicationForm.setValue("applicant.phoneNumber",applicationData?.applicant?.phoneNumber);
        applicationForm.setFocus("applicant.notes");
        applicationForm.setValue("applicant.notes",applicationData?.applicant?.notes);
        applicationForm.setFocus("applicant.newPartner");
        applicationForm.setValue("applicant.newPartner",applicationData?.applicant?.newPartner);
    },[applicationData])

    useEffect(() => {
    }, [applicationForm])
    return (
        <>
                <form onSubmit={applicationForm.handleSubmit(onSubmit)}>
                    <Grid container direction="row" alignItems="start">
                        <Grid item xs={12}>
                             { hasScope( NetworkScope.MsMng) &&
                                <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                                    { isDisabled ? <Tooltip title="Edit">
                                        <BorderColorIcon
                                            style={{
                                                color: "#C8017D"
                                            }}
                                            onClick={handleDisableForm}
                                        />
                                    </Tooltip> : null }
                                </Stack>
                             }
                        </Grid>
                        <Grid container item direction="row" xs={12} columnSpacing={2}>
                            <Grid item xs={3}>
                                <FormTextField
                                    disabled
                                    control={applicationForm.control}
                                    name="applicationIdentifyingCode"
                                    label={t(`searchApplication.detail.form.label.applicationIdentifyingCode`) }// "Application identification code"
                                />

                            </Grid>

                            <Grid item xs={3}>
                                <FormTextField
                                    disabled
                                    control={applicationForm.control}
                                    name="communicatedToAuthoritiesDate"
                                    label={t(`searchApplication.detail.form.label.communicatedToAuthoritiesDate`)}// "communicated to authorities date"
                                />
                            </Grid>

                            <Grid item xs={3}>
                                <FormAutocomplete
                                    disabled
                                    control={applicationForm.control}
                                    name="currentStatus"
                                    options={mockCurrentStatus}
                                    label={t(`searchApplication.detail.form.label.currentStatus`)} // "Current Status"
                                    isOptionEqualToValue={(option, value) => option === value}
                                    getOptionLabel={(opt) => t(`searchApplication.form.label.currentStatusValue.${opt}`)}
                                    // onChange={handleChangeCurrentStatus}
                                    // onInputChange={handleChangeCurrentStatus}

                                />

                            </Grid>
                            <Grid item xs={3}>
                                <FormAutocomplete
                                    control={applicationForm.control}
                                    name="evaluationOutcome"
                                    disabled={evaluationOutcomeDisabled}
                                    options={evaluationOutcomeList}
                                    label={t(`searchApplication.detail.form.label.evaluationOutcome`)} // "Evaluation outcome"
                                />
                            </Grid>

                            <Grid item xs={3}>
                                <FormAutocomplete
                                    disabled
                                    control={applicationForm.control}
                                    name="areaOfApplicationStatus"
                                    label={t(`searchApplication.detail.form.label.areaOfApplicationStatus`)} // "Area of application status"
                                    options={mockAreaOfApplicationStatus}
                                />
                            </Grid>

                            <Grid item xs={3}>
                                <FormTextField
                                    disabled={isDisabled}
                                    control={applicationForm.control}
                                    name="applicant.address"
                                    type={"text"}
                                    label={t(`searchApplication.detail.form.label.applicant.address`)}// "Address"
                                />

                            </Grid>
                            <Grid item xs={3}>
                                <FormTextField
                                    disabled={isDisabled}
                                    control={applicationForm.control}
                                    name="applicant.city"
                                    type={"text"}
                                    label={t(`searchApplication.detail.form.label.applicant.city`)}// "City"
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <FormAutocomplete
                                    disabled
                                    control={applicationForm.control}
                                    name="applicant.region"
                                    options={mockRegionList}
                                    label={t(`searchApplication.detail.form.label.applicant.region`)} // "Region"
                                />
                            </Grid>

                            <Grid item xs={3}>
                                <FormTextField
                                    disabled={isDisabled}
                                    control={applicationForm.control}
                                    name="applicant.companyName"
                                    label={t(`searchApplication.detail.form.label.applicant.companyName`)} // "Company name"
                                />
                            </Grid>

                            <Grid item xs={3}>
                                <FormTextField
                                    disabled={isDisabled}
                                    control={applicationForm.control}
                                    name="applicant.email"
                                    label={t(`searchApplication.detail.form.label.applicant.email`)} // "Email"
                                />

                            </Grid>
                            <Grid item xs={3}>
                                <FormTextField
                                    disabled
                                    control={applicationForm.control}
                                    name="applicant.name"
                                    label={t(`searchApplication.detail.form.label.applicant.name`)} // "Name"
                                />

                            </Grid>
                            <Grid item xs={3}>
                                <FormTextField
                                    disabled
                                    control={applicationForm.control}
                                    name="applicant.surname"
                                    label={t(`searchApplication.detail.form.label.applicant.surname`)} // "Surname"
                                />

                            </Grid>
                            <Grid item xs={3}>
                                <FormAutocomplete
                                    disabled={isDisabled}
                                    control={applicationForm.control}
                                    name="applicant.newPartner"
                                    options={mockNewPartner}
                                    label={t(`searchApplication.detail.form.label.applicant.newPartner`)} // "New partner"
                                    getOptionLabel={(opt) => opt === true ? 'Yes' : 'No'}
                                    isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}

                                />

                            </Grid>
                            <Grid item container xs={3} columnSpacing={2}>
                                <Grid item xs={6}>
                                    <FormAutocomplete
                                        disabled={isDisabled}
                                        control={applicationForm.control}
                                        name="prefixPhoneNumber"
                                        label={t(`searchApplication.detail.form.label.prefixPhoneNumber`)} // {"Prefix"}
                                        options={countriesPrefix}
                                        getOptionLabel={(option: any) => option?.code + " +" + option?.description}
                                        isOptionEqualToValue={(option, value) => option?.code === value?.code}
                                        renderOption={(props, option) => (
                                            <Box
                                                component="li"
                                                {...props}
                                            >
                                                {option?.code} +{option?.description}
                                            </Box>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <FormTextField
                                        disabled={isDisabled}
                                        control={applicationForm.control}
                                        name="applicant.phoneNumber"
                                        label={t(`searchApplication.detail.form.label.applicant.phoneNumber`)}  // "Phone number"
                                    />
                                </Grid>

                            </Grid>
                            <Grid item xs={3}>
                                <FormTextField
                                    disabled={isDisabled}
                                    control={applicationForm.control}
                                    name="applicant.province"
                                    label={t(`searchApplication.detail.form.label.applicant.province`)} // "Province"
                                />

                            </Grid>
                            <Grid item xs={3}>
                                <FormTextField
                                    disabled={isDisabled}
                                    control={applicationForm.control}
                                    name="postalCode"
                                    label={t(`searchApplication.detail.form.label.postalCode`)} // "Postal Code"
                                />

                            </Grid>
                            <Grid item xs={3}>
                                <FormTextField
                                    disabled={isDisabled}
                                    control={applicationForm.control}
                                    name="applicant.vatNumber"
                                    label={t(`searchApplication.detail.form.label.applicant.vatNumber`)} //"VAT number"
                                />

                            </Grid>

                            <Grid item xs={3}>
                                <FormTextField
                                    disabled={isDisabled}
                                    control={applicationForm.control}
                                    name="applicant.siren"
                                    label={t(`searchApplication.detail.form.label.applicant.siren`)} //"SIREN"
                                />

                            </Grid>

                            <Grid item xs={3}>
                                <FormTextField
                                    disabled={isDisabled}
                                    control={applicationForm.control}
                                    name="applicant.siret"
                                    label={t(`searchApplication.detail.form.label.applicant.siret`)} //"SIRET"
                                />

                            </Grid>

                            <Grid item xs={3}>
                                <FormTextField
                                    disabled={isDisabled}
                                    control={applicationForm.control}
                                    name="applicant.taxCode"
                                    label={t(`searchApplication.detail.form.label.applicant.taxCode`)} //"Fiscal Code"
                                />

                            </Grid>

                            <Grid item xs={6}>
                                <FormTextField
                                    disabled={isDisabled}
                                    control={applicationForm.control}
                                    name="applicant.notes"
                                    label={t(`searchApplication.detail.form.label.applicant.notes`)} // "Notes"
                                />

                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                                    { !isDisabled ? <Button variant="contained" onClick={handleOpen} >
                                        Modifica
                                    </Button> : null }
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>
                </form>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Confirm changes?"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to confirm the changes?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button autoFocus type={"submit"} onClick={applicationForm.handleSubmit(onSubmit)}> Confirm </Button>
                    <Button onClick={handleClose}> Cancel </Button>
                </DialogActions>
            </Dialog>

        </>


    )
}
export default Application;